/**
 * Shared /chart/$ticker and /technical/$ticker registration.
 *
 * Params parse, validateSearch, crumbs, Suspense and the error boundary are
 * the app's untrusted-input surface — one implementation so the two screens
 * cannot diverge.
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type ComponentType, type JSX } from 'react';
import { z } from 'zod';

import {
  RequestedTickerSchema,
  type StockSearchParams,
  type Ticker,
  type WindowKey,
} from '@meridian/contracts';

import { Breadcrumbs } from '@/components/chrome/breadcrumbs';
import { Skeleton } from '@/components/primitives/skeleton';
import { InvalidTickerParamError } from '@/features/stock/invalid-ticker-param-error';
import { TickerRouteError } from '@/features/stock/ticker-route-error';
import { chartStudyHrefs } from '@/lib/nav/chart-studies';
import { hrefForSearch } from '@/lib/nav/search';
import { dashboardTarget } from '@/lib/nav/targets';
import { rootRoute } from '@/router-root';

const paramsSchema = z.object({
  ticker: RequestedTickerSchema,
});

export function parseTickerRouteParams(raw: Record<string, string>): {
  readonly ticker: Ticker;
} {
  const result = paramsSchema.safeParse({ ticker: raw['ticker'] });
  if (!result.success) {
    throw new InvalidTickerParamError();
  }
  return result.data;
}

export interface TickerDrilldownScreenProps {
  readonly ticker: Ticker;
  readonly window: WindowKey;
  readonly range: WindowKey | undefined;
  readonly onRangeChange: (range: WindowKey) => void;
  readonly studyHrefs: ReturnType<typeof chartStudyHrefs>;
}

export type ReplaceSearch = (next: object) => void;

export interface TickerDrilldownRouteOptions<
  TSearch extends StockSearchParams,
  TProps extends TickerDrilldownScreenProps,
> {
  readonly path: '/chart/$ticker' | '/technical/$ticker';
  readonly crumbLabel: string;
  readonly skeletonLabel: string;
  readonly load: () => Promise<Record<string, unknown>>;
  readonly pick: (loaded: Record<string, unknown>) => ComponentType<TProps>;
  readonly parseSearch: (search: Record<string, unknown>) => TSearch;
  readonly extraProps?: (ctx: {
    readonly search: TSearch;
    readonly replaceSearch: ReplaceSearch;
  }) => Omit<TProps, keyof TickerDrilldownScreenProps>;
}

function drilldownCrumbs(
  ticker: Ticker,
  search: StockSearchParams,
  crumbLabel: string,
): readonly { readonly label: string; readonly href?: string }[] {
  const hrefs = chartStudyHrefs(ticker, search);
  const dash = dashboardTarget(search);
  return [
    { label: 'Dashboard', href: hrefForSearch('/', dash.search) },
    { label: ticker, href: hrefs.stock },
    { label: crumbLabel },
  ];
}

function DrilldownShell<TProps extends TickerDrilldownScreenProps>(props: {
  readonly ticker: Ticker;
  readonly search: StockSearchParams;
  readonly crumbLabel: string;
  readonly skeletonLabel: string;
  readonly Screen: ComponentType<TProps>;
  readonly extra: object;
  readonly onRangeChange: (range: WindowKey) => void;
}): JSX.Element {
  const screenProps = {
    ticker: props.ticker,
    window: props.search.win,
    range: props.search.range,
    onRangeChange: props.onRangeChange,
    studyHrefs: chartStudyHrefs(props.ticker, props.search),
    ...props.extra,
  } as TProps;
  return (
    <Suspense fallback={<Skeleton height="18rem" label={props.skeletonLabel} />}>
      <Breadcrumbs items={drilldownCrumbs(props.ticker, props.search, props.crumbLabel)} />
      <props.Screen {...screenProps} />
    </Suspense>
  );
}

/** Route instance type is inferred from `createRoute`; writing it out fights the router generics. */
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- Route<...> is unspeakable */
export function createTickerDrilldownRoute<
  TSearch extends StockSearchParams,
  TProps extends TickerDrilldownScreenProps,
>(options: TickerDrilldownRouteOptions<TSearch, TProps>) {
  const Screen = lazy(async () => {
    const loaded = await options.load();
    return { default: options.pick(loaded) };
  });
  const route = createRoute({
    getParentRoute: () => rootRoute,
    path: options.path,
    params: {
      parse: (raw) => parseTickerRouteParams(raw),
      stringify: (p) => ({ ticker: p.ticker }),
    },
    validateSearch: (search: Record<string, unknown>) => options.parseSearch(search),
    loader: options.load,
    component: DrilldownAdapter,
    errorComponent: TickerRouteError,
  });

  function DrilldownAdapter(): JSX.Element {
    const { ticker } = route.useParams();
    const search = route.useSearch() as TSearch;
    const navigate = useNavigate({ from: route.fullPath });
    const onRangeChange = useCallback(
      (range: WindowKey) => {
        void navigate({ search: (prev) => ({ ...prev, range }), replace: true });
      },
      [navigate],
    );
    const replaceSearch = useCallback<ReplaceSearch>(
      (next) => {
        void navigate({ search: () => next as never, replace: true });
      },
      [navigate],
    );
    return (
      <DrilldownShell
        ticker={ticker}
        search={search}
        crumbLabel={options.crumbLabel}
        skeletonLabel={options.skeletonLabel}
        Screen={Screen}
        extra={options.extraProps?.({ search, replaceSearch }) ?? {}}
        onRangeChange={onRangeChange}
      />
    );
  }

  return route;
}
